import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchDecision, updateDecision, createDecision, deleteDecision } from "Redux/Admin/DecisionManager/Decisions/action";

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { IDecision } from 'Redux/Admin/DecisionManager/Decisions/type';

const Decision = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchDecisions);

  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchDecision(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createDecision(values, handleCreate));
    }
    else if (id) {
      dispatch(updateDecision(values, id));
    }
  };

  const handleCreate = (project: IDecision) => {
    if (project && project.id) {
      navigate(`${ADMIN_PATH}/decision-manager/decisions/${project.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/decision-manager/decisions`, { replace: true });
    return null;
  }

  const decision = isNew ? initialValues : entities[id];

  if (!decision) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'Delete Decision',
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the decision?',
    content: <span>Are you sure you want to delete the decision <b>{decision.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteDecision(id, () => {
          navigate(`${ADMIN_PATH}/decision-manager/decisions`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/decision-manager/decisions`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } Decision`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Decision
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={decision}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default Decision;
